import React, { useState } from 'react';
import { string, object, bool, func, array, arrayOf } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { NamedLink, LoginPromptModal } from '../../../components';
import CompactListingCard from '../../../components/ListingCard/CompactListingCard';
import { ensureListing } from '../../../util/data';
import { addFavorite, removeFavorite, getFavoriteListings } from '../../../ducks/favorites.duck';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import css from './SectionFeaturedListings.module.css';

const SectionFeaturedListingsComponent = props => {
  const {
    className,
    rootClassName,
    listings,
    intl,
    title,
    isSearchMode,
    currentUser,
    favoriteListings,
    onAddFavorite,
    onRemoveFavorite,
    onManageDisableScrolling,
    filterUrl,
    seeAllRouteName,
    seeAllLabelId,
    emptyLabelId,
  } = props;

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  const classes = classNames(rootClassName || css.root, className, {
    [css.searchModeRoot]: isSearchMode,
  });

  // Eligibility is enforced by each data source before listings reach this presentation component.
  const visibleListings = Array.isArray(listings) ? listings : [];

  // Don't limit listings when in search mode - show all results
  // Limit the homepage projection to five; search mode receives its already-paginated page.
  const searchListings = isSearchMode ? visibleListings : visibleListings.slice(0, 5);
  const hasListings = searchListings.length > 0;

  const handleBookmarkClick = listingId => {
    // Check if user is authenticated and fully loaded
    if (!currentUser || !currentUser.attributes) {
      setIsLoginModalOpen(true);
      return;
    }

    const isFavorited = Array.isArray(favoriteListings) && favoriteListings.includes(listingId);
    if (isFavorited) {
      onRemoveFavorite(listingId);
    } else {
      onAddFavorite(listingId);
    }
  };

  return (
    <section className={classes}>
      <div className={css.container}>
        {!isSearchMode && (
          <div className={css.header}>
            <h2 className={css.title}>
              {title || <FormattedMessage id="SectionFeaturedListings.title" />}
            </h2>
            {filterUrl ? (
              <a href={filterUrl} className={css.seeAllButton}>
                Filter & Sort
              </a>
            ) : (
              <NamedLink name={seeAllRouteName} className={css.seeAllButton}>
                <FormattedMessage id={seeAllLabelId} />
              </NamedLink>
            )}
          </div>
        )}

        {hasListings ? (
          <div className={classNames(css.listingsGrid, { [css.searchModeGrid]: isSearchMode })}>
            {searchListings.map(listing => {
              const currentListing = ensureListing(listing);
              const listingId = currentListing.id.uuid;
              const isFavorited =
                Array.isArray(favoriteListings) && favoriteListings.includes(listingId);

              return (
                <CompactListingCard
                  key={listingId}
                  listing={listing}
                  isFavorited={isFavorited}
                  onBookmarkClick={handleBookmarkClick}
                />
              );
            })}
          </div>
        ) : (
          <div className={css.noListings}>
            <p>
              <FormattedMessage id={emptyLabelId} />
            </p>
          </div>
        )}
      </div>

      <LoginPromptModal
        id="SectionFeaturedListings.loginPrompt"
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </section>
  );
};

SectionFeaturedListingsComponent.defaultProps = {
  className: null,
  rootClassName: null,
  listings: null,
  title: null,
  isSearchMode: false,
  currentUser: null,
  seeAllRouteName: 'SearchPage',
  seeAllLabelId: 'SectionFeaturedListings.seeAllListings',
  emptyLabelId: 'SectionFeaturedListings.noListings',
  favoriteListings: [],
};

SectionFeaturedListingsComponent.propTypes = {
  className: string,
  rootClassName: string,
  listings: arrayOf(object),
  title: string,
  isSearchMode: bool,
  intl: intlShape.isRequired,
  currentUser: object,
  favoriteListings: array,
  onAddFavorite: func.isRequired,
  onRemoveFavorite: func.isRequired,
  seeAllRouteName: string,
  seeAllLabelId: string,
  emptyLabelId: string,
  onManageDisableScrolling: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const favoriteListings = getFavoriteListings(state);

  return {
    currentUser,
    favoriteListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onAddFavorite: listingId => dispatch(addFavorite(listingId)),
  onRemoveFavorite: listingId => dispatch(removeFavorite(listingId)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const SectionFeaturedListings = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(SectionFeaturedListingsComponent);

export default SectionFeaturedListings;
